import React, { useState, useEffect } from 'react';
import {
    Heart,
    HandCoins,
    Users,
    Target,
    Globe,
    Shield,
    ArrowRight,
    CheckCircle,
    Smartphone,
    Building2,
    CreditCard,
    Zap,
    Award,
    Clock,
    Star,
    BookOpen,
    Mic,
    Landmark,
    GraduationCap,
    HeartPulse,
    Siren,
    PlusCircle,
    Radio,
    Microscope,
    Loader2,
    User,
    MessageSquare,
    Leaf,
    Sprout,
    TrendingUp,
    FileText
} from 'lucide-react';
import './Donate.css';

// ============================================
// TYPES
// ============================================

interface Category {
    _id?: string;
    name: string;
    slug: string;
    description: string;
    icon: string;
    color: string;
    isActive: boolean;
    sortOrder: number;
}

interface PaymentMethod {
    _id?: string;
    name: string;
    slug: string;
    type: 'mobile_money' | 'bank_transfer' | 'card' | 'other';
    provider?: string;
    icon: string;
    isActive: boolean;
    sortOrder: number;
    instructions?: string;
    isInternational?: boolean;
    hoverColor?: string;
}

// ============================================
// ICON MAP
// ============================================

const ICON_MAP: Record<string, any> = {
    Landmark,
    FileText,
    Sprout,
    TrendingUp,
    Leaf,
    Heart,
    PlusCircle,
    Building2,
    Microscope,
    Radio,
    GraduationCap,
    HeartPulse,
    Siren,
    Globe,
    Users,
    HandCoins,
    BookOpen,
    Mic,
    Target,
    Shield,
    Award,
    Zap,
    Smartphone,
    CreditCard
};

// ============================================
// DEFAULT DATA (Fallback if API fails)
// ============================================

const DEFAULT_CATEGORIES: Category[] = [
    { 
        name: 'Radio Programs', 
        slug: 'radio-programs', 
        description: 'Community radio and awareness campaigns reaching thousands across Zambia', 
        icon: 'Radio', 
        color: '#ED1C24', 
        isActive: true, 
        sortOrder: 1 
    },
    { 
        name: 'Governance & Accountability', 
        slug: 'governance', 
        description: 'Strengthening democratic processes and accountability', 
        icon: 'Landmark', 
        color: '#8B5CF6', 
        isActive: true, 
        sortOrder: 2 
    },
    { 
        name: 'Policy Analysis & Advocacy', 
        slug: 'policy-advocacy', 
        description: 'Research and advocacy for progressive policies', 
        icon: 'FileText', 
        color: '#3B82F6', 
        isActive: true, 
        sortOrder: 3 
    },
    { 
        name: 'Agricultural Support', 
        slug: 'agriculture', 
        description: 'Supporting small-scale farmers and food security', 
        icon: 'Sprout', 
        color: '#10B981', 
        isActive: true, 
        sortOrder: 4 
    },
    { 
        name: 'Socio-Economic Empowerment', 
        slug: 'socio-economic', 
        description: 'Economic empowerment and livelihood programs', 
        icon: 'TrendingUp', 
        color: '#F59E0B', 
        isActive: true, 
        sortOrder: 5 
    },
    { 
        name: 'Environmental Sustainability', 
        slug: 'environment', 
        description: 'Conservation and sustainable practices', 
        icon: 'Leaf', 
        color: '#22C55E', 
        isActive: true, 
        sortOrder: 6 
    },
    { 
        name: 'Cultural & Remembrance', 
        slug: 'cultural-remembrance', 
        description: 'Cultural heritage and remembrance programs', 
        icon: 'Heart', 
        color: '#EC4899', 
        isActive: true, 
        sortOrder: 7 
    },
    { 
        name: 'General Fund', 
        slug: 'general', 
        description: 'Support our most critical needs', 
        icon: 'PlusCircle', 
        color: '#6B7280', 
        isActive: true, 
        sortOrder: 8 
    }
];

const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = [
    { 
        name: 'Airtel Money', 
        slug: 'airtel-money', 
        type: 'mobile_money', 
        provider: 'Airtel', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 1, 
        isInternational: false,
        hoverColor: '#FF0000'
    },
    { 
        name: 'MTN Mobile Money', 
        slug: 'mtn-money', 
        type: 'mobile_money', 
        provider: 'MTN', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 2, 
        isInternational: false,
        hoverColor: '#FFCC00'
    },
    { 
        name: 'Zamtel Money', 
        slug: 'zamtel-money', 
        type: 'mobile_money', 
        provider: 'Zamtel', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 3, 
        isInternational: false,
        hoverColor: '#00A651'
    },
    { 
        name: 'ZedMobile', 
        slug: 'zedmobile', 
        type: 'mobile_money', 
        provider: 'ZedMobile', 
        icon: 'Smartphone', 
        isActive: true, 
        sortOrder: 4, 
        isInternational: false,
        hoverColor: '#7B2FBE'
    },
    { 
        name: 'Bank Transfer', 
        slug: 'bank-transfer', 
        type: 'bank_transfer', 
        icon: 'Building2', 
        isActive: true, 
        sortOrder: 5, 
        isInternational: true,
        hoverColor: '#1E40AF',
        instructions: 'Use the bank details provided at checkout to complete your transfer'
    },
    { 
        name: 'Card Payment', 
        slug: 'card-payment', 
        type: 'card', 
        icon: 'CreditCard', 
        isActive: true, 
        sortOrder: 6, 
        isInternational: true,
        hoverColor: '#0EA5E9',
        instructions: 'Visa, Mastercard, and other major cards accepted securely'
    }
];

// ============================================
// COMPONENT
// ============================================

export const Donate: React.FC = () => {
    // ============================================
    // STATE
    // ============================================
    
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(DEFAULT_PAYMENT_METHODS);
    const [error, setError] = useState<string | null>(null);
    
    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [customAmount, setCustomAmount] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('radio-programs');
    const [selectedMethod, setSelectedMethod] = useState<string>('airtel-money');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [donorName, setDonorName] = useState('');
    const [donorEmail, setDonorEmail] = useState('');
    const [donorPhone, setDonorPhone] = useState('');
    const [message, setMessage] = useState('');
    const [isMonthly, setIsMonthly] = useState(false);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [hoveredMethod, setHoveredMethod] = useState<string | null>(null);

    const quickAmounts = [50, 100, 250, 500, 1000];

    // ============================================
    // FETCH DYNAMIC DATA
    // ============================================
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const categoriesRes = await fetch('/.netlify/functions/admin-categories');
                if (categoriesRes.ok) {
                    const categoriesData = await categoriesRes.json();
                    if (categoriesData.success && categoriesData.data) {
                        setCategories(categoriesData.data);
                    }
                }
                
                const methodsRes = await fetch('/.netlify/functions/admin-payment-methods');
                if (methodsRes.ok) {
                    const methodsData = await methodsRes.json();
                    if (methodsData.success && methodsData.data) {
                        setPaymentMethods(methodsData.data);
                    }
                }
            } catch (err) {
                console.warn('Failed to fetch dynamic data, using defaults:', err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, []);

    // ============================================
    // HELPERS
    // ============================================
    
    const getCategoryIcon = (iconName: string): any => {
        return ICON_MAP[iconName] || PlusCircle;
    };

    const getPaymentMethodIcon = (iconName: string): any => {
        return ICON_MAP[iconName] || Smartphone;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setTimeout(() => {
            setIsSubmitting(false);
            alert('Thank you for your donation! We will process it shortly.');
        }, 1500);
    };

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }).map((_, index) => (
            <Star
                key={index}
                size={16}
                fill={index < rating ? '#f59e0b' : 'none'}
                color={index < rating ? '#f59e0b' : '#d1d5db'}
            />
        ));
    };

    const activeCategories = categories
        .filter(c => c.isActive)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const activePaymentMethods = paymentMethods
        .filter(m => m.isActive)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    // ============================================
    // RENDER
    // ============================================
    
    if (loading) {
        return (
            <div className="donate-page">
                <div className="loading-state" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader2 size={48} className="spinner" />
                    <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading donation options...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="donate-page">
            {/* Hero Section */}
            <section className="donate-hero">
                <div className="container">
                    <div className="hero-grid">
                        <div className="hero-content">
                            <div className="hero-badge">
                                <Heart size={16} />
                                Every Contribution Counts
                            </div>
                            <h1>
                                Support{' '}
                                <span className="gradient-text">Let's Change Zambia</span>
                            </h1>
                            <p className="hero-description">
                                Your generous donation helps us create lasting change in Zambia.
                                Together, we can build a brighter future for our communities.
                            </p>
                            <div className="hero-stats">
                                <div className="stat">
                                    <span className="stat-number">15,000+</span>
                                    <span className="stat-label">Lives Impacted</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-number">50+</span>
                                    <span className="stat-label">Communities</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-number">12</span>
                                    <span className="stat-label">Years of Service</span>
                                </div>
                            </div>
                            <div className="hero-cta">
                                <a href="#donate-form" className="btn-hero-primary">
                                    Donate Now <ArrowRight size={20} />
                                </a>
                                <a href="#impact" className="btn-hero-secondary">
                                    Learn More
                                </a>
                            </div>
                        </div>
                        <div className="hero-visual">
                            <div className="floating-card card-1">
                                <Heart size={24} color="#ed1c24" />
                                <span>100% Transparency</span>
                            </div>
                            <div className="floating-card card-2">
                                <Target size={24} color="#ed1c24" />
                                <span>Direct Impact</span>
                            </div>
                            <div className="floating-card card-3">
                                <Globe size={24} color="#ed1c24" />
                                <span>Global Reach</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact Section */}
            <section className="impact-section" id="impact">
                <div className="container">
                    <div className="section-header">
                        <h2>Your Impact in Action</h2>
                        <p>See how your donations are making a difference in communities across Zambia.</p>
                    </div>
                    <div className="impact-grid">
                        <div className="impact-card">
                            <div className="impact-icon-wrapper">
                                <BookOpen size={32} />
                            </div>
                            <h3>Education</h3>
                            <p>Supporting 1,200+ students with educational materials and scholarships.</p>
                            <div className="impact-progress">
                                <div className="progress-bar" style={{ width: '75%' }} />
                            </div>
                            <span className="progress-label">75% of target reached</span>
                        </div>
                        <div className="impact-card">
                            <div className="impact-icon-wrapper">
                                <Mic size={32} />
                            </div>
                            <h3>Radio Programs</h3>
                            <p>Reaching 500,000+ listeners with community awareness programs.</p>
                            <div className="impact-progress">
                                <div className="progress-bar" style={{ width: '60%' }} />
                            </div>
                            <span className="progress-label">60% of target reached</span>
                        </div>
                        <div className="impact-card">
                            <div className="impact-icon-wrapper">
                                <Landmark size={32} />
                            </div>
                            <h3>Governance</h3>
                            <p>Strengthening democratic processes in 15 districts across Zambia.</p>
                            <div className="impact-progress">
                                <div className="progress-bar" style={{ width: '45%' }} />
                            </div>
                            <span className="progress-label">45% of target reached</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Donation Form */}
            <section className="donate-form-section" id="donate-form">
                <div className="container">
                    <div className="form-container">
                        <div className="form-header">
                            <h2>Make a Donation</h2>
                            <p>Choose an amount and where you want your donation to go.</p>
                        </div>
                        <form onSubmit={handleSubmit} className="donate-form">
                            <div className="form-grid">
                                <div className="form-main">
                                    {/* Personal Information */}
                                    <div className="form-group">
                                        <label>
                                            <User size={16} />
                                            Your Information
                                        </label>
                                        <div className="form-row">
                                            <input
                                                type="text"
                                                placeholder="Full Name"
                                                value={donorName}
                                                onChange={(e) => setDonorName(e.target.value)}
                                                required
                                            />
                                            <input
                                                type="email"
                                                placeholder="Email Address"
                                                value={donorEmail}
                                                onChange={(e) => setDonorEmail(e.target.value)}
                                                required
                                            />
                                            <input
                                                type="tel"
                                                placeholder="Phone Number"
                                                value={donorPhone}
                                                onChange={(e) => setDonorPhone(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Amount Selection */}
                                    <div className="form-group">
                                        <label>
                                            <HandCoins size={16} />
                                            Select Amount (ZMW)
                                        </label>
                                        <div className="amount-options">
                                            {quickAmounts.map((amount) => (
                                                <button
                                                    key={amount}
                                                    type="button"
                                                    className={`amount-option ${selectedAmount === amount ? 'active' : ''}`}
                                                    onClick={() => setSelectedAmount(amount)}
                                                >
                                                    K{amount}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className={`amount-option custom ${selectedAmount === null && customAmount ? 'active' : ''}`}
                                                onClick={() => setSelectedAmount(null)}
                                            >
                                                Custom
                                            </button>
                                        </div>
                                        {selectedAmount === null && (
                                            <div className="custom-amount-input">
                                                <input
                                                    type="number"
                                                    placeholder="Enter amount..."
                                                    value={customAmount}
                                                    onChange={(e) => setCustomAmount(e.target.value)}
                                                    min="1"
                                                />
                                                <span className="currency-suffix">ZMW</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Category Selection - DYNAMIC */}
                                    <div className="form-group">
                                        <label>
                                            <Target size={16} />
                                            Choose a Program to Support
                                        </label>
                                        <div className="category-grid">
                                            {activeCategories.map((category) => {
                                                const Icon = getCategoryIcon(category.icon);
                                                return (
                                                    <button
                                                        key={category.slug}
                                                        type="button"
                                                        className={`category-option ${selectedCategory === category.slug ? 'active' : ''}`}
                                                        onClick={() => setSelectedCategory(category.slug)}
                                                        style={{
                                                            borderColor: selectedCategory === category.slug ? category.color : undefined,
                                                            backgroundColor: selectedCategory === category.slug ? `${category.color}15` : undefined
                                                        }}
                                                    >
                                                        <Icon size={20} style={{ color: category.color }} />
                                                        <span className="category-label">{category.name}</span>
                                                        <span className="category-desc">{category.description}</span>
                                                        {selectedCategory === category.slug && (
                                                            <CheckCircle size={16} className="check-icon" style={{ color: category.color }} />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-sidebar">
                                    {/* Payment Method Selection - DYNAMIC with Hover Effect */}
                                    <div className="sidebar-card">
                                        <h3>Payment Method</h3>
                                        <div className="payment-options">
                                            {activePaymentMethods.map((method) => {
                                                const Icon = getPaymentMethodIcon(method.icon);
                                                const isHovered = hoveredMethod === method.slug;
                                                const hoverStyle = isHovered && method.hoverColor ? {
                                                    backgroundColor: `${method.hoverColor}20`,
                                                    borderColor: method.hoverColor,
                                                    transform: 'scale(1.02)',
                                                    boxShadow: `0 4px 12px ${method.hoverColor}40`
                                                } : {};
                                                
                                                return (
                                                    <button
                                                        key={method.slug}
                                                        type="button"
                                                        className={`payment-option ${selectedMethod === method.slug ? 'active' : ''}`}
                                                        onClick={() => setSelectedMethod(method.slug)}
                                                        onMouseEnter={() => setHoveredMethod(method.slug)}
                                                        onMouseLeave={() => setHoveredMethod(null)}
                                                        style={hoverStyle}
                                                    >
                                                        <Icon size={20} />
                                                        <span>{method.name}</span>
                                                        {method.isInternational && (
                                                            <span className="international-badge">
                                                                <Globe size={14} className="international-icon" />
                                                            </span>
                                                        )}
                                                        {selectedMethod === method.slug && (
                                                            <CheckCircle size={16} className="check-icon" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="sidebar-card">
                                        <h3>Options</h3>
                                        <div className="checkbox-group">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isMonthly}
                                                    onChange={(e) => setIsMonthly(e.target.checked)}
                                                />
                                                <span>Make this a monthly donation</span>
                                            </label>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isAnonymous}
                                                    onChange={(e) => setIsAnonymous(e.target.checked)}
                                                />
                                                <span>Donate anonymously</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="sidebar-card">
                                        <label htmlFor="message">
                                            <MessageSquare size={16} />
                                            Message (Optional)
                                        </label>
                                        <textarea
                                            id="message"
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Leave a message of support..."
                                            rows={3}
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn-donate-submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 size={20} className="spinner" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <HandCoins size={20} />
                                                Donate Now
                                                <ArrowRight size={20} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {/* Trust Section */}
            <section className="trust-section">
                <div className="container">
                    <div className="trust-grid">
                        <div className="trust-item">
                            <Shield size={32} />
                            <h4>100% Transparent</h4>
                            <p>We track every Kwacha and share regular reports with our donors.</p>
                        </div>
                        <div className="trust-item">
                            <Award size={32} />
                            <h4>Registered NGO</h4>
                            <p>Officially registered and compliant with Zambian law.</p>
                        </div>
                        <div className="trust-item">
                            <Users size={32} />
                            <h4>Community-Driven</h4>
                            <p>Our work is guided by the needs of the communities we serve.</p>
                        </div>
                        <div className="trust-item">
                            <Zap size={32} />
                            <h4>Direct Impact</h4>
                            <p>Your donation goes directly to programs that create real change.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="testimonials-section">
                <div className="container">
                    <div className="section-header">
                        <h2>What Our Supporters Say</h2>
                    </div>
                    <div className="testimonials-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-quote">"</div>
                            <p>I've been supporting this organization for three years. The transparency and impact are incredible.</p>
                            <div className="testimonial-author">
                                <span className="author-name">Chabala M.</span>
                                <span className="author-location">Lusaka, Zambia</span>
                            </div>
                            <div className="testimonial-stars">
                                {renderStars(5)}
                            </div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-quote">"</div>
                            <p>I love how they involve the community in every step. My monthly donation is making a real difference.</p>
                            <div className="testimonial-author">
                                <span className="author-name">Malambo K.</span>
                                <span className="author-location">Copperbelt, Zambia</span>
                            </div>
                            <div className="testimonial-stars">
                                {renderStars(5)}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Make a Difference?</h2>
                        <p>Every donation, no matter the size, helps us create lasting change in Zambia.</p>
                        <a href="#donate-form" className="btn-cta-primary">
                            <Heart size={20} />
                            Donate Now
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Donate;